import type { QuizDocument, QuizQuestion, QuizScore } from "./types.js";

export interface QuizImportInput {
  schemaVersion?: unknown;
  title?: unknown;
  sourceName?: unknown;
  sourcePages?: unknown;
  difficulty?: unknown;
  questions?: unknown;
}

function requiredText(value: unknown, label: string, maxLength: number): string {
  const text = String(value ?? "").trim();
  if (!text) throw new Error(`${label}不能留空。`);
  if (text.length > maxLength) throw new Error(`${label}不能超過 ${maxLength} 字。`);
  return text;
}

function optionalText(value: unknown, maxLength: number): string | undefined {
  const text = String(value ?? "").trim();
  if (!text) return undefined;
  if (text.length > maxLength) throw new Error(`文字欄位不能超過 ${maxLength} 字。`);
  return text;
}

export function validateQuizImport(
  input: QuizImportInput,
  context: { workspaceId: string; id: string; now?: string; classId?: string },
): QuizDocument {
  if (input.schemaVersion !== undefined && Number(input.schemaVersion) !== 1) {
    throw new Error("目前只支援 schemaVersion 1。");
  }
  if (!Array.isArray(input.questions) || input.questions.length < 1 || input.questions.length > 100) {
    throw new Error("題組必須包含 1 至 100 題。");
  }
  const questions: QuizQuestion[] = input.questions.map((raw, index) => {
    if (!raw || typeof raw !== "object") throw new Error(`第 ${index + 1} 題格式不正確。`);
    const item = raw as Record<string, unknown>;
    if (!Array.isArray(item.options) || item.options.length !== 4) {
      throw new Error(`第 ${index + 1} 題必須有四個選項。`);
    }
    const answer = Number(item.correctIndex);
    if (!Number.isInteger(answer) || answer < 0 || answer > 3) {
      throw new Error(`第 ${index + 1} 題的 correctIndex 必須是 0 至 3。`);
    }
    return {
      id: String(item.id || `q${index + 1}`),
      question: requiredText(item.question, `第 ${index + 1} 題題幹`, 1000),
      options: item.options.map((option, optionIndex) =>
        requiredText(option, `第 ${index + 1} 題選項 ${optionIndex + 1}`, 500),
      ) as [string, string, string, string],
      correctIndex: answer as 0 | 1 | 2 | 3,
      explanation: requiredText(item.explanation, `第 ${index + 1} 題解釋`, 2000),
      sourcePageLabel: optionalText(item.sourcePageLabel, 100),
      evidence: optionalText(item.evidence, 2000),
      knowledgePoint: optionalText(item.knowledgePoint, 300),
      cognitiveLevel: ["remember", "understand", "apply", "analyze"].includes(String(item.cognitiveLevel))
        ? (item.cognitiveLevel as QuizQuestion["cognitiveLevel"])
        : undefined,
    };
  });
  const now = context.now ?? new Date().toISOString();
  const difficulty = ["foundation", "standard", "advanced"].includes(String(input.difficulty))
    ? (input.difficulty as QuizDocument["difficulty"])
    : "standard";
  return {
    schemaVersion: 1,
    id: context.id,
    workspaceId: context.workspaceId,
    classId: context.classId,
    title: requiredText(input.title, "題組名稱", 120),
    sourceName: optionalText(input.sourceName, 200),
    sourcePages: optionalText(input.sourcePages, 100),
    difficulty,
    questions,
    createdAt: now,
    updatedAt: now,
  };
}

export function normalizeAnswers(raw: unknown, questionCount: number): number[] {
  const source = Array.isArray(raw) ? raw : [];
  return Array.from({ length: questionCount }, (_, index) => {
    const answer = source[index];
    return typeof answer === "number" && Number.isInteger(answer) && answer >= 0 && answer <= 3 ? answer : -1;
  });
}

export function resolveSubmissionAnswers(
  savedAnswers: unknown,
  submittedAnswers: unknown,
  questionCount: number,
  locked: boolean,
): number[] {
  return normalizeAnswers(locked ? savedAnswers : submittedAnswers, questionCount);
}

export function scoreQuiz(questions: QuizQuestion[], rawAnswers: unknown): QuizScore {
  const answers = normalizeAnswers(rawAnswers, questions.length);
  const correct = questions.map((question, index) => question.correctIndex === answers[index]);
  const score = correct.filter(Boolean).length;
  return {
    score,
    questionCount: questions.length,
    percent: questions.length ? Math.round((score / questions.length) * 1000) / 10 : 0,
    correct,
  };
}
